import { TASK_TYPE_DISCIPLINE_MAP } from '../constants/visitTypes';
import type { DisciplineType } from '../services/api/models/DisciplineType';
import type {
  EpisodeLupaPeriodResponse,
  VisitTypeId,
} from '../services/api/types.gen';
import { formatDateToISO, parseISOToLocalDate } from '../utils/dateUtils';
import { EPISODE_START_DEFINING_VISIT_TYPES } from '../utils/episodeBoundaryVisits';

/** One staged visit type paired with the dates it should be created on. */
export interface StagedVisitGroup {
  visitType: VisitTypeId;
  /** Alias the visits are scheduled under; unset means the type's own name. */
  aliasId?: string;
  displayName: string;
  /** Unique, ascending. */
  dates: Date[];
}

/** One visit in the bulk-create payload. */
export interface BulkVisitItem {
  visit_type: VisitTypeId;
  alias_id?: string | null;
  discipline: DisciplineType;
  visit_date: string;
  assigned_clinician_id: string;
}

export const dedupeSortedDates = (dates: Date[]): Date[] => {
  const byIso = new Map<string, Date>();
  for (const date of dates) byIso.set(formatDateToISO(date), date);
  return [...byIso.values()].sort((a, b) => a.getTime() - b.getTime());
};

/**
 * A staged group's picker key: the visit type, or ``alias:<id>`` for a group
 * scheduled under an alias, matching the alias option ids the picker uses.
 */
export const stagedGroupKey = (group: StagedVisitGroup): string =>
  group.aliasId ? `alias:${group.aliasId}` : group.visitType;

/**
 * Set the exact dates for a visit type (or one of its aliases) in the staged
 * list. Each name appears at most once: an empty date list removes it,
 * otherwise its dates are replaced (an existing group keeps its position; a
 * new one is appended).
 */
export const setDatesForType = (
  groups: StagedVisitGroup[],
  visitType: VisitTypeId,
  displayName: string,
  dates: Date[],
  aliasId?: string
): StagedVisitGroup[] => {
  const key = stagedGroupKey({ visitType, aliasId, displayName, dates });
  const sorted = dedupeSortedDates(dates);
  if (sorted.length === 0) {
    return groups.filter(group => stagedGroupKey(group) !== key);
  }
  if (groups.some(group => stagedGroupKey(group) === key)) {
    return groups.map(group =>
      stagedGroupKey(group) === key
        ? { ...group, displayName, dates: sorted }
        : group
    );
  }
  return [
    ...groups,
    { visitType, ...(aliasId ? { aliasId } : {}), displayName, dates: sorted },
  ];
};

export const countVisits = (groups: StagedVisitGroup[]): number =>
  groups.reduce((total, group) => total + group.dates.length, 0);

/** How many staged dates are OASIS Start of Care (any discipline). */
export const countOasisSocVisits = (groups: StagedVisitGroup[]): number =>
  groups.reduce(
    (total, group) =>
      total +
      (EPISODE_START_DEFINING_VISIT_TYPES.has(group.visitType)
        ? group.dates.length
        : 0),
    0
  );

/**
 * Staged dates that will count toward LUPA, by period number: dates of a
 * billable visit type inside the period's window. `isBillable` answers for
 * the agency, since a new visit's billable status is fixed by its type.
 */
export const countNewBillableVisitsByPeriod = (
  groups: StagedVisitGroup[],
  periods: ReadonlyArray<
    Pick<EpisodeLupaPeriodResponse, 'period_number' | 'start_date' | 'end_date'>
  >,
  isBillable: (visitType: VisitTypeId) => boolean
): Map<number, number> => {
  const counts = new Map<number, number>();
  const windows = periods.flatMap(period => {
    const start = parseISOToLocalDate(period.start_date);
    const end = parseISOToLocalDate(period.end_date);
    return start && end ? [{ number: period.period_number, start, end }] : [];
  });
  for (const group of groups) {
    if (!isBillable(group.visitType)) continue;
    for (const date of group.dates) {
      const window = windows.find(w => date >= w.start && date <= w.end);
      if (window) {
        counts.set(window.number, (counts.get(window.number) ?? 0) + 1);
      }
    }
  }
  return counts;
};

/** Flatten staged groups into the bulk endpoint's `visits` payload. Every item
 * is assigned to `assignedClinicianId` (the dialog currently uses one assignee
 * for the whole batch; the payload carries it per item so different assignees
 * per visit can be supported later without an API change). */
export const buildBulkVisitItems = (
  groups: StagedVisitGroup[],
  assignedClinicianId: string
): BulkVisitItem[] =>
  groups.flatMap(group =>
    group.dates.map(date => ({
      visit_type: group.visitType,
      ...(group.aliasId ? { alias_id: group.aliasId } : {}),
      discipline: TASK_TYPE_DISCIPLINE_MAP[group.visitType],
      visit_date: formatDateToISO(date),
      assigned_clinician_id: assignedClinicianId,
    }))
  );
